import { useEffect, useMemo } from 'react';

import { useLazyQuery } from '@apollo/client/react';
import { useGraphQLQuery } from '@betterment/graphql';
import { partition } from 'es-toolkit/compat';

import { getLazyLoadingStatus, getLoadingStatus } from '~/b4b/graphql/helpers';

import { GetEmployerSelectDocument, GetEmployerSelectQuery } from './graphql/query.getEmployerSelect.generated';
import { GetEmployerSelectWithTasksDocument } from './graphql/query.getEmployerSelectWithTasks.generated';

export { type LoadingStatus } from '~/b4b/graphql/helpers';

export type Employer = GetEmployerSelectQuery['employers'][number];

const isActive = (employer: Employer) => {
  return employer.plan?.status === 'ACTIVE' || employer.plan?.status === 'ONBOARDING';
};

export const useEmployerSelectData = () => {
  const query = useGraphQLQuery(GetEmployerSelectDocument, { throwOnError: true });
  const [loadTasks, tasksQuery] = useLazyQuery(GetEmployerSelectWithTasksDocument);

  const { data: rawData } = query;
  const data = rawData as GetEmployerSelectQuery | undefined;

  const loadingStatus = getLoadingStatus(query);
  const tasksLoadingStatus = getLazyLoadingStatus(tasksQuery);

  const employers = useMemo(() => {
    const unsorted = data?.employers.filter(Boolean) || [];
    return unsorted.slice().sort((a, b) => a.name.localeCompare(b.name));
  }, [data]);

  const [activeEmployers, inactiveEmployers] = useMemo(() => partition(employers, isActive), [employers]);

  const taskCounts: Record<string, number> = useMemo(() => {
    return (tasksQuery.data?.employers || []).reduce(
      (acc, employer) => {
        if (employer?.id && employer?.tasks?.count !== undefined) {
          acc[employer.id] = employer.tasks.count;
        }
        return acc;
      },
      {} as Record<string, number>
    );
  }, [tasksQuery.data]);

  // Auto-fire tasks query after employers load
  useEffect(() => {
    if (loadingStatus === 'loaded' && tasksLoadingStatus === 'idle' && employers.length !== 1) {
      void loadTasks();
    }
  }, [loadingStatus, tasksLoadingStatus, employers.length, loadTasks]);

  return {
    activeEmployers,
    inactiveEmployers,
    taskCounts,
    loadingStatus,
    tasksLoadingStatus,
  };
};
